/**
 * Pamięć zaznaczonego elementu na ekran — czyli powrót na stronę główną
 * w to samo miejsce, z którego się wyszło.
 *
 * Bez tego przejście pilotem wygląda tak: zjeżdżasz na czwarty pas, przechodzisz
 * na szósty kafel, wchodzisz w tytuł, wracasz — i jesteś z powrotem na górze strony,
 * z całą drogą do przejścia jeszcze raz. Myszą tego problemu nie ma, bo wskaźnik
 * wraca tam, gdzie jest ręka; pilotem wraca się tam, gdzie fokus.
 *
 * Element rozpoznajemy z **tego, co już ma**, a nie z identyfikatora dopisanego do
 * każdego kafla. Kafel tytułu jest odnośnikiem na `/tytul/<ulid>`, więc jego `href`
 * jest kluczem stabilnym także po przebudowaniu drzewa — a to jest warunek, żeby
 * cokolwiek dało się odnaleźć po powrocie na ekran, który zmontował się od nowa.
 * Ta sama zasada, na której stoi nawigacja strzałkami: żywy DOM, zero owijania
 * komponentów.
 */

/** Ile ekranów pamiętamy. Zapas jest po to, żeby droga w tył miała gdzie wracać. */
export const MAX_REMEMBERED_PATHS = 20;

/** To, co element o sobie mówi — w kolejności zaufania. */
export interface FocusKeySource {
  /** `data-focus-key`: jawna deklaracja z komponentu, gdy `href` nie wystarcza. */
  focusKey?: string | null | undefined;
  href?: string | null | undefined;
  id?: string | null | undefined;
}

/**
 * Klucz elementu albo `null`, gdy element nie ma nic, czym dałoby się go odnaleźć.
 *
 * Klucz nosi przedrostek rodzaju, bo `/tytul/x` jako `href` i jako `id` to dwa różne
 * elementy — bez przedrostka pamięć jednego ekranu wskazywałaby na drugi.
 */
export function focusKey(source: FocusKeySource): string | null {
  const candidates: Array<[string, string | null | undefined]> = [
    ['key', source.focusKey],
    ['href', source.href],
    ['id', source.id],
  ];

  for (const [kind, value] of candidates) {
    if (value === null || value === undefined || value === '') continue;
    // Wartość z cudzysłowem albo odwrotnym ukośnikiem zepsułaby selektor, a nie ma
    // takiej w adresach aplikacji — więc odrzucamy zamiast escapować.
    if (value.includes('"') || value.includes('\\')) continue;
    return `${kind}:${value}`;
  }
  return null;
}

/** Selektor odnajdujący element po kluczu. */
export function selectorForKey(key: string): string | null {
  const separator = key.indexOf(':');
  if (separator <= 0) return null;
  const kind = key.slice(0, separator);
  const value = key.slice(separator + 1);
  if (value.length === 0) return null;

  switch (kind) {
    case 'key':
      return `[data-focus-key="${value}"]`;
    case 'href':
      return `[href="${value}"]`;
    // `[id=…]`, nie `#…`: identyfikator z kropką albo dwukropkiem jest legalny
    // w HTML, a w selektorze wymagałby escapowania.
    case 'id':
      return `[id="${value}"]`;
    default:
      return null;
  }
}

/**
 * Pamięć „ekran → ostatni fokus", z limitem i odświeżaniem przy użyciu.
 *
 * Trzymana w pamięci karty, nie w `localStorage`: to stan przechodzenia po
 * aplikacji, a nie preferencja. Zapisana na dysku kazałaby po tygodniu skakać na
 * kafel, którego nikt nie pamięta — i mogłaby wskazywać materiał już usunięty.
 */
export class FocusMemory {
  private readonly byPath = new Map<string, string>();

  remember(path: string, key: string): void {
    // Usunięcie przed wstawieniem przesuwa wpis na koniec, więc limit zdejmuje
    // ekran najdawniej używany, a nie najdawniej odwiedzony pierwszy raz.
    this.byPath.delete(path);
    this.byPath.set(path, key);

    while (this.byPath.size > MAX_REMEMBERED_PATHS) {
      const oldest = this.byPath.keys().next();
      if (oldest.done === true) break;
      this.byPath.delete(oldest.value);
    }
  }

  recall(path: string): string | null {
    return this.byPath.get(path) ?? null;
  }

  forget(path: string): void {
    this.byPath.delete(path);
  }

  get size(): number {
    return this.byPath.size;
  }
}

/**
 * Czym widz posługuje się w tej chwili.
 *
 * Przywracanie fokusu ma sens **tylko przy strzałkach**. Przy myszy przeniosłoby
 * widok w inne miejsce strony i pokazało pierścień fokusu na kaflu, którego nikt nie
 * dotykał — czyli naprawiałoby problem, którego mysz nie ma, kosztem zaskoczenia.
 */
let directional = false;

export function noteDirectionalInput(): void {
  directional = true;
}

export function notePointerInput(): void {
  directional = false;
}

export function isDirectionalInput(): boolean {
  return directional;
}
